import { Link, NavLink, Outlet } from "react-router-dom";
import { ChatDock } from "./components/ChatDock";
import { Disclaimer } from "./components/Disclaimer";
import { ThemeToggle } from "./components/ThemeToggle";
import { displayName, loginUrl, LOGOUT_URL, PROVIDERS, useProfile } from "./lib/auth";

const NAV = [
  { to: "/dashboard", label: "Overview", end: true },
  { to: "/themes", label: "Themes" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/personas", label: "Personas" },
  { to: "/sim", label: "Simulator" },
  { to: "/backtest", label: "Model check" },
  { to: "/method", label: "Method" },
];

export function App() {
  return (
    <div className="min-h-full flex flex-col">
      <header
        className="border-b sticky top-0 z-20"
        style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3 pt-4">
            <div className="flex items-baseline gap-3">
              <Link to="/" className="text-base font-semibold tracking-tight">
                Stock Market Blitz
              </Link>
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                equity factor research
              </span>
            </div>
            <div className="flex items-center gap-3">
              <AccountStrip />
              <ThemeToggle />
            </div>
          </div>
          <nav className="flex gap-1 -mb-px pt-3 overflow-x-auto">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-2 text-sm border-b-2 transition-colors ${
                    isActive ? "font-medium" : ""
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                  borderColor: isActive ? "var(--series-1)" : "transparent",
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 w-full flex-1">
        <Outlet />
      </main>

      <ChatDock />
      <Disclaimer />
    </div>
  );
}

/**
 * Identity, when there is one.
 *
 * Signing in is optional: the site is public and nothing here requires an
 * account. A profile appears the moment any provider is used, which is what
 * makes per-user portfolio storage possible — and is deliberately separate
 * from access to the chat endpoint, which spends money per request and stays
 * on an explicit allowlist.
 */
function AccountStrip() {
  const { profile, loading } = useProfile();
  if (loading) return null;

  if (!profile) {
    return (
      <span className="text-xs flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
        <span>Not signed in</span>
        {PROVIDERS.map((p) => (
          <a
            key={p.id}
            href={loginUrl(p.id, window.location.href)}
            className="underline underline-offset-2"
            style={{ color: "var(--series-1)" }}
          >
            {p.label}
          </a>
        ))}
      </span>
    );
  }

  return (
    <span className="text-xs flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
      <span title={`${profile.userDetails} via ${profile.identityProvider}`}>
        Signed in as{" "}
        <span style={{ color: "var(--text-secondary)" }}>{displayName(profile)}</span>
      </span>
      <a href={LOGOUT_URL} className="underline underline-offset-2" style={{ color: "var(--series-1)" }}>
        Sign out
      </a>
    </span>
  );
}
